// src/services/socketClient.js
import { io } from 'socket.io-client';
import authService from './authService';
import browserDetection from '../utils/browserDetection';

// Manager-level events (socket.io-client v4) - emitted by the Manager
// instance (`socket.io`), never by the Socket itself (confirmed directly
// against node_modules/socket.io-client/build/esm/manager.js). Registering
// these via socket.on(...) silently never fires; on()/off() below route
// them to socket.io instead.
const MANAGER_EVENTS = new Set([
  'reconnect',
  'reconnect_attempt',
  'reconnect_error',
  'reconnect_failed',
]);

class SocketClient {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
    // Reconnection state for exponential backoff
    this.reconnectionAttempts = 0;
    this.maxReconnectionAttempts = 10;
    this.baseReconnectionDelay = 1000; // Start with 1 second
    this.maxReconnectionDelay = 30000; // Max 30 seconds
    this.lastRoomCode = null;
    this.lastUsername = null;
    this.lastIsSpectator = false;
    // COLD START FIX: Connection status callbacks for UX feedback
    this.connectionStatusCallbacks = [];
    this.connectionStartTime = null;
  }

  connect() {
    if (this.socket && this.isConnected) {
      return this.socket;
    }

    // Use current domain origin in production (since frontend & backend are unified)
    const serverURL =
      process.env.REACT_APP_API_URL ||
      process.env.REACT_APP_BACKEND_URL ||
      (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3001');

    console.log(`🔌 Attempting to connect to: ${serverURL}`);

    // COLD START FIX: Track connection start time
    this.connectionStartTime = Date.now();
    this.notifyConnectionStatus('connecting', { message: 'Connecting to server...' });

    // Detect Firefox, Edge, and other browsers with WebSocket issues
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isEdge = navigator.userAgent.toLowerCase().includes('edg');

    // Firefox and Edge-specific connection options
    // Edge has issues with WebSocket during navigation, use conservative settings
    const needsConservativeSettings = isFirefox || isEdge;

    // Get JWT token from authService for authentication
    const authToken = authService.getToken();

    const connectionOptions = {
      // For Firefox/Edge, prioritize polling and be more conservative with upgrade
      transports: needsConservativeSettings ? ['polling'] : ['polling', 'websocket'],
      upgrade: !needsConservativeSettings, // Don't auto-upgrade for Firefox/Edge
      // COLD START FIX: Increased timeout to 45s to handle Render free tier cold starts (30+ seconds)
      timeout: 45000, // 45 seconds for all browsers (was 20-30s)
      forceNew: false,
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: needsConservativeSettings ? 2000 : 1000, // Slower reconnection for Firefox/Edge
      reconnectionDelayMax: 10000,
      reconnectionAttempts: 10, // Increased to 10 attempts for all browsers (was 5-10)
      withCredentials: true,
      // JWT authentication - include token in handshake
      auth: authToken ? { token: authToken } : {},
      // Firefox/Edge-specific polling configuration
      ...(needsConservativeSettings && {
        upgradeTimeout: 30000, // Give Firefox/Edge more time to upgrade
        pingTimeout: 60000,
        pingInterval: 25000,
      }),
    };

    console.log(`🦊 Browser detection: Firefox=${isFirefox}, Safari=${isSafari}, Edge=${isEdge}`);
    console.log(`🔧 Using connection options:`, connectionOptions);
    if (authToken) {
      console.log(`🔐 Connecting with JWT authentication`);
    } else {
      console.log(`🔓 Connecting as guest (no JWT token)`);
    }

    this.socket = io(serverURL, connectionOptions);

    // Flush any listeners queued by on() while this.socket was still null
    // (e.g. useGameWebSocket's mount-time registration, which reliably runs
    // before this point on every mount - confirmed live: all 38 app event
    // registrations end up queued here, every time). This used to only
    // happen inside the 'connect' handler below, which meant a connection
    // that never succeeds even once (all Finding 11/12 testing) left every
    // queued listener - including reconnect_failed itself - stranded
    // forever. The Socket/Manager objects accept .on() the instant they
    // exist, connected or not, so there's no reason to wait for 'connect'.
    this.applyQueuedListeners();

    this.socket.on('connect', () => {
      console.log('✅ Connected to game server:', this.socket.id);
      console.log('✅ Transport method:', this.socket.io.engine.transport.name);
      this.isConnected = true;

      // COLD START FIX: Calculate connection time and notify about cold starts
      const connectionTime = this.connectionStartTime ? Date.now() - this.connectionStartTime : 0;
      const wasColdStart = connectionTime > 10000; // 10+ seconds = cold start

      if (wasColdStart) {
        console.log(
          `🥶 Cold start detected: ${Math.round(connectionTime / 1000)}s connection time`
        );
        this.notifyConnectionStatus('connected', {
          message: 'Server ready!',
          wasColdStart: true,
          connectionTime,
        });
      } else {
        this.notifyConnectionStatus('connected', {
          message: 'Connected',
          wasColdStart: false,
          connectionTime,
        });
      }

      this.connectionStartTime = null; // Reset

      // REMOVED: Firefox WebSocket upgrade probe - caused crashes on server restart
      // Socket.io will handle transport upgrades automatically
      if (isFirefox) {
        console.log('🦊 Firefox using:', this.socket.io.engine.transport.name);
      } else if (isEdge) {
        console.log('🌐 Edge using:', this.socket.io.engine.transport.name);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Disconnected from server:', reason);
      this.isConnected = false;

      // FIXED: Clear room data on intentional client disconnect to prevent auto-rejoin
      if (reason === 'io client disconnect') {
        console.log('🔌 Intentional disconnect - clearing room data');
        this.lastRoomCode = null;
        this.lastUsername = null;
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('🔥 Connection error:', error);
      console.error('🔥 Error details:', error.message);
      console.error('🔥 Error type:', error.type);
      console.error('🔥 Error description:', error.description);
      this.isConnected = false;

      // #237: DuckDuckGo's privacy protections block WebSocket connections -
      // surface a specific, actionable log line instead of a generic error,
      // since this is the single most common cause of "can't connect" reports.
      if (browserDetection.isDuckDuckGo()) {
        console.error(
          '⚠️ DuckDuckGo Browser Detected: WebSocket connections are being blocked by privacy protection. ' +
            'Please use Chrome, Firefox, Safari, or Edge for multiplayer games.'
        );
      }

      // COLD START FIX: Show user-friendly message if connection is taking a while
      const connectionTime = this.connectionStartTime ? Date.now() - this.connectionStartTime : 0;
      if (connectionTime > 10000 && error.message && error.message.includes('timeout')) {
        this.notifyConnectionStatus('cold-start', {
          message: 'Waking up server... This may take up to 30 seconds',
          connectionTime,
        });
      } else {
        this.notifyConnectionStatus('error', {
          message: error.message || 'Connection error',
          error,
        });
      }
    });

    // FIXED: Add transport upgrade logging
    this.socket.on('upgrade', () => {
      console.log('🚀 Upgraded to transport:', this.socket.io.engine.transport.name);
    });

    // FIXED: Add reconnection event handlers
    // These four are Manager-level events (socket.io-client v4) - the
    // Manager emits them via `this.emitReserved(...)` internally (confirmed
    // directly against node_modules/socket.io-client/build/esm/manager.js),
    // never on the Socket itself. Registering them on `this.socket.on(...)`
    // silently no-ops forever; they must be registered on `this.socket.io`
    // (the Manager instance). The event name is also `reconnect_attempt`,
    // not `reconnecting`.
    this.socket.io.on('reconnect', (attemptNumber) => {
      console.log('🔄 Reconnected after', attemptNumber, 'attempts');
      this.isConnected = true;
      this.reconnectionAttempts = 0; // Reset custom reconnection attempts

      // Auto-rejoin room if we have the info. Routed through the same
      // join-room path a manual rejoin already uses (addPlayer's existing
      // reconnect-matching logic), instead of the removed player-reconnected
      // event - this gets the same score/hand/owner/judge restoration a
      // manual rejoin gets, plus join-room's own built-in timeout and error
      // handling, so no bespoke error listener is needed here anymore.
      if (this.lastRoomCode && this.lastUsername) {
        console.log('🔄 Auto-rejoining room after reconnection...');
        this.joinRoom(this.lastRoomCode, this.lastUsername, null, null, this.lastIsSpectator).catch(
          (error) => {
            console.warn('⚠️ Auto-rejoin failed (room may no longer exist):', error.message);
            // Clear stored room data since rejoin failed
            this.lastRoomCode = null;
            this.lastUsername = null;
          }
        );
      }
    });

    this.socket.io.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Reconnecting... attempt', attemptNumber);
    });

    this.socket.io.on('reconnect_error', (error) => {
      console.error('❌ Reconnection error:', error);
    });

    this.socket.io.on('reconnect_failed', () => {
      console.error('❌ Reconnection failed - max attempts reached');
      this.notifyConnectionStatus('reconnect_failed', {
        message: 'Unable to reconnect. Click Reconnect to try again.',
      });
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // After socket.io's own reconnection attempts are exhausted ('reconnect_failed'),
  // it gives up permanently and never retries on its own. Calling connect() again
  // on the same socket re-opens it and, if that attempt also fails, resumes the
  // normal reconnection backoff cycle rather than staying dead forever.
  manualReconnect() {
    if (this.socket) {
      console.log('🔄 Manual reconnect requested');
      this.socket.connect();
    } else {
      this.connect();
    }
  }

  // Tell the server this is a deliberate leave, not a dropped connection -
  // must be called before disconnect() so it's sent over the still-open
  // socket, otherwise the server can't distinguish it from any other drop.
  leaveRoom(roomCode) {
    this.emit('leave-room', { roomCode: roomCode.toUpperCase() });
  }

  // Emit method for sending events to backend
  emit(event, data) {
    if (!this.socket || !this.isConnected) {
      console.error('Cannot emit - socket not connected');
      return;
    }

    console.log(`📤 Emitting ${event}:`, JSON.stringify(data, null, 2));
    this.socket.emit(event, data);
  }

  // Join a game room
  joinRoom(roomCode, username, token, settings = null, isSpectator = false) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('Not connected to server'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Join room timeout'));
      }, 10000);

      // Send join request with correct parameters
      const joinData = {
        username,
        token,
      };

      // If roomCode is provided, use it. If null, backend will auto-generate
      if (roomCode) {
        joinData.roomCode = roomCode.toUpperCase();
        // Store room info for auto-reconnection
        this.lastRoomCode = roomCode.toUpperCase();
      }

      // Include settings if provided (for room creation)
      if (settings) {
        joinData.settings = settings;
      }

      // Include spectator status
      if (isSpectator) {
        joinData.isSpectator = true;
      }

      this.lastUsername = username;
      this.lastIsSpectator = isSpectator;

      this.socket.emit('join-room', joinData);

      // Listen for successful join responses
      this.socket.once('joined-room', (data) => {
        clearTimeout(timeout);
        console.log('🎮 Successfully joined room:', data);

        // Store generated room code if it was auto-generated
        if (!roomCode && data.roomCode) {
          this.lastRoomCode = data.roomCode;
        }

        resolve(data.roomCode || roomCode);
      });

      this.socket.once('room-updated', (data) => {
        clearTimeout(timeout);
        console.log('🎮 Room updated:', data);

        // Store generated room code if it was auto-generated
        if (!roomCode && data.roomCode) {
          this.lastRoomCode = data.roomCode;
        }

        resolve(data.roomCode || roomCode);
      });

      this.socket.once('game-started', (data) => {
        clearTimeout(timeout);
        console.log('🎮 Joined active game:', data);

        // Store generated room code if it was auto-generated
        if (!roomCode && data.roomCode) {
          this.lastRoomCode = data.roomCode;
        }

        resolve(data.roomCode || roomCode);
      });

      // Listen for error response
      this.socket.once('error', (error) => {
        clearTimeout(timeout);
        console.error('❌ Failed to join room:', error);
        reject(new Error(error.message || error || 'Failed to join room'));
      });
    });
  }

  // Start game
  startGame(roomCode) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('Not connected to server'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Start game timeout'));
      }, 10000);

      this.socket.emit('start-game', { roomCode });

      this.socket.once('game-started', (gameState) => {
        clearTimeout(timeout);
        console.log('🎮 Game started:', gameState);
        resolve(gameState);
      });

      this.socket.once('error', (error) => {
        clearTimeout(timeout);
        reject(new Error(error.message || error || 'Failed to start game'));
      });
    });
  }

  // Get current game state
  getGameState(roomCode) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('Not connected to server'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Get game state timeout'));
      }, 5000);

      this.socket.emit('get-game-state', { roomCode });

      this.socket.once('game-state', (gameState) => {
        clearTimeout(timeout);
        resolve(gameState);
      });

      this.socket.once('error', (error) => {
        clearTimeout(timeout);
        reject(new Error(error.message || error || 'Failed to get game state'));
      });
    });
  }

  // Add event listener
  on(event, callback) {
    if (!this.socket) {
      console.warn('Socket not connected, queuing listener for:', event);
      // Store for when socket connects
      if (!this.listeners.has(event)) {
        this.listeners.set(event, []);
      }
      this.listeners.get(event).push(callback);
      return;
    }

    const target = MANAGER_EVENTS.has(event) ? this.socket.io : this.socket;
    target.on(event, callback);
  }

  // Remove event listener
  off(event, callback) {
    if (this.socket) {
      const target = MANAGER_EVENTS.has(event) ? this.socket.io : this.socket;
      target.off(event, callback);
    }
  }

  // Apply queued listeners when socket connects
  applyQueuedListeners() {
    this.listeners.forEach((callbacks, event) => {
      const target = MANAGER_EVENTS.has(event) ? this.socket.io : this.socket;
      callbacks.forEach((callback) => {
        target.on(event, callback);
      });
    });
    this.listeners.clear();
  }

  // Check connection status
  isSocketConnected() {
    return this.socket && this.isConnected;
  }

  // Get socket ID
  getSocketId() {
    return this.socket ? this.socket.id : null;
  }

  // Check if already joined a specific room
  isAlreadyInRoom(roomCode) {
    return (
      this.isConnected &&
      this.lastRoomCode &&
      this.lastRoomCode.toUpperCase() === roomCode.toUpperCase()
    );
  }

  // Get the current room code
  getCurrentRoomCode() {
    return this.lastRoomCode;
  }

  // FIXED: Add manual connection trigger method
  forceConnect() {
    console.log('🔄 Force connecting to server...');
    if (this.socket) {
      this.socket.connect();
    } else {
      this.connect();
    }
  }

  // FIXED: Add connection test method
  testConnection() {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        this.connect();
      }

      if (this.isConnected) {
        resolve(true);
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Connection test timeout'));
      }, 5000);

      this.socket.once('connect', () => {
        clearTimeout(timeout);
        resolve(true);
      });

      this.socket.once('connect_error', (error) => {
        clearTimeout(timeout);
        reject(error);
      });

      if (!this.socket.connected) {
        this.socket.connect();
      }
    });
  }

  // Request hand refill
  requestHandRefill(roomCode, username) {
    if (!this.socket || !this.isConnected) {
      console.error('Cannot request hand refill - socket not connected');
      return;
    }

    console.log('📤 Requesting hand refill:', { roomCode, username });
    this.socket.emit('request-hand-refill', { roomCode, username });
  }

  // Manual reconnection with exponential backoff
  attemptManualReconnection() {
    return new Promise((resolve, reject) => {
      if (this.reconnectionAttempts >= this.maxReconnectionAttempts) {
        reject(new Error('Maximum reconnection attempts reached'));
        return;
      }

      const delay = Math.min(
        this.baseReconnectionDelay * Math.pow(2, this.reconnectionAttempts),
        this.maxReconnectionDelay
      );

      console.log(
        `🔄 Manual reconnection attempt ${this.reconnectionAttempts + 1}/${this.maxReconnectionAttempts}, waiting ${delay}ms...`
      );

      this.reconnectionAttempts++;

      setTimeout(() => {
        if (this.isConnected) {
          resolve(true);
          return;
        }

        // If socket exists but not connected, try to reconnect
        if (this.socket) {
          this.socket.connect();
        } else {
          this.connect();
        }

        // Set up listeners for this attempt
        const timeout = setTimeout(() => {
          reject(new Error(`Reconnection attempt ${this.reconnectionAttempts} timed out`));
        }, 10000);

        const onConnect = () => {
          clearTimeout(timeout);
          this.socket.off('connect_error', onError);
          resolve(true);
        };

        const onError = (error) => {
          clearTimeout(timeout);
          this.socket.off('connect', onConnect);
          reject(error);
        };

        this.socket.once('connect', onConnect);
        this.socket.once('connect_error', onError);
      }, delay);
    });
  }

  // Reset reconnection state (call when successfully connected and in game)
  resetReconnectionState() {
    this.reconnectionAttempts = 0;
  }

  // COLD START FIX: Connection status notification methods
  onConnectionStatus(callback) {
    this.connectionStatusCallbacks.push(callback);
    // Return unsubscribe function
    return () => this.offConnectionStatus(callback);
  }

  offConnectionStatus(callback) {
    const index = this.connectionStatusCallbacks.indexOf(callback);
    if (index > -1) {
      this.connectionStatusCallbacks.splice(index, 1);
    }
  }

  notifyConnectionStatus(status, data) {
    this.connectionStatusCallbacks.forEach((callback) => {
      try {
        callback(status, data);
      } catch (error) {
        console.error('Error in connection status callback:', error);
      }
    });
  }
}

// Create singleton instance
const socketClient = new SocketClient();

export default socketClient;
